import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/HoDashboard.jsx
// HO treasury overview: KPI cards, 7-day trend chart, branch summary table.
// Branch users see an access-denied message.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Typography, Spin, Alert, Row, Col, Button,
  DatePicker, Space, Divider,
} from 'antd';
import { ReloadOutlined, LockOutlined, SettingOutlined } from '@ant-design/icons';
import { Column } from '@ant-design/charts';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById } from '../../branches/api';
import {
  fetchHoDashboardSummary,
  fetchHoDashboardBranches,
  fetchHoDashboardTrend,
} from '../api';
import TreasuryKpiCards from '../components/TreasuryKpiCards';
import BranchSummaryTable from '../components/BranchSummaryTable';

const { Title, Text } = Typography;

export default function HoDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isHO, setIsHO] = useState(false);
  const [initLoading, setInitLoading] = useState(true);

  const [selectedDate, setSelectedDate] = useState(dayjs());

  const [summary, setSummary] = useState(null);
  const [branchRows, setBranchRows] = useState([]);
  const [trendData, setTrendData] = useState([]);

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Init: resolve branch type ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        setIsHO(branch?.branchType === 'HO' || isGlobalViewer(user));
      } catch {
        setError('Failed to load branch profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  // ── Data loaders ───────────────────────────────────────────────────────────
  const dateParam = selectedDate ? selectedDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await fetchHoDashboardSummary({ businessDate: dateParam });
      setSummary(res?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [dateParam]);

  const loadBranches = useCallback(async () => {
    setBranchesLoading(true);
    try {
      const res = await fetchHoDashboardBranches({ businessDate: dateParam, limit: 200 });
      setBranchRows(res?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load branch data');
    } finally {
      setBranchesLoading(false);
    }
  }, [dateParam]);

  const loadTrend = useCallback(async () => {
    setTrendLoading(true);
    try {
      const res = await fetchHoDashboardTrend({ days: 7 });
      const raw = res?.data?.data || [];
      const chartData = [];
      raw.forEach(({ date, cashIn, cashOut }) => {
        chartData.push({ date: dayjs(date).format('DD/MM'), type: 'Cash In', amount: cashIn });
        chartData.push({ date: dayjs(date).format('DD/MM'), type: 'Cash Out', amount: cashOut });
      });
      setTrendData(chartData);
    } catch {
      // non-critical
    } finally {
      setTrendLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initLoading && isHO) {
      loadSummary();
      loadBranches();
    }
  }, [selectedDate, initLoading, isHO]);

  useEffect(() => {
    if (!initLoading && isHO) loadTrend();
  }, [initLoading, isHO]);

  const handleRefresh = () => {
    setError(null);
    loadSummary();
    loadBranches();
    loadTrend();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  if (!isHO) {
    return (
      <div style={{ padding: '80px 0', textAlign: 'center' }}>
        <LockOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
        <Title level={5} type="secondary">HO Dashboard</Title>
        <Text type="secondary">This section is restricted to Head Office users.</Text>
      </div>
    );
  }

  const trendConfig = {
    data: trendData,
    isGroup: true,
    xField: 'date',
    yField: 'amount',
    seriesField: 'type',
    color: ['#52c41a', '#f5222d'],
    legend: { position: 'top-right' },
    tooltip: {
      formatter: (datum) => ({
        name: datum.type,
        value: `₹${Number(datum.amount || 0).toLocaleString('en-IN')}`,
      }),
    },
    yAxis: {
      label: {
        formatter: (v) => {
          if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
          if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
          return `₹${v}`;
        },
      },
    },
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>Treasury — HO Dashboard</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {selectedDate ? selectedDate.format('dddd, DD MMM YYYY') : 'Today'}
          </Text>
        </Col>
        <Col>
          <Space>
            <DatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              format="DD/MM/YYYY"
              allowClear={false}
            />
            <Button
              icon={<SettingOutlined />}
              onClick={() => navigate('/treasury/settings')}
            >
              Settings
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={summaryLoading || branchesLoading}
            >
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ── KPI Cards ── */}
      <TreasuryKpiCards summary={summary} loading={summaryLoading} />

      <Divider style={{ margin: '20px 0 16px' }} />

      {/* ── 7-Day Cash Trend ── */}
      <Card
        size="small"
        title="7-Day Cash Movement"
        style={{ marginBottom: 16 }}
        loading={trendLoading}
      >
        {trendData.length > 0 ? (
          <Column {...trendConfig} height={220} />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#bfbfbf' }}>
            No transaction data in the last 7 days
          </div>
        )}
      </Card>

      {/* ── Branch Summary Table ── */}
      <Card
        size="small"
        title={
          <span>
            Branch Summary
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
              {selectedDate ? selectedDate.format('DD MMM YYYY') : 'Today'}
            </Text>
          </span>
        }
      >
        <BranchSummaryTable rows={branchRows} loading={branchesLoading} />
      </Card>
    </div>
  );
}
